import { attachmentKind, type AttachmentKind } from './attachment-kind';
import { AttachmentFileTypeIcon } from './attachment-filetype-icon';
import { AttachmentImageButton } from './attachment-image';
import { AttachmentViewer } from './attachment-viewer';
import type { EventAttachment } from './event-attachments';
import { useAttachmentViewer } from './use-attachment-viewer';
import { useAttachmentDownload } from './use-attachment-download';

interface RowProps {
  attachment: EventAttachment;
  onOpen: () => void;
  downloading: boolean;
  onDownload: () => void;
}

function AttachmentFileRow({ attachment, onOpen, downloading, onDownload }: RowProps) {
  const canOpen = Boolean(attachment.sourceItemId);

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-2 text-left text-xs text-text-secondary disabled:opacity-50"
        onClick={canOpen ? onOpen : undefined}
        disabled={!canOpen}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white/[0.03]">
          <AttachmentFileTypeIcon attachment={attachment} />
        </span>
        <span className="truncate">{attachment.fileName ?? 'Vedlegg'}</span>
      </button>
      <button
        type="button"
        onClick={onDownload}
        disabled={!attachment.sourceItemId || downloading}
        className="rounded-md border border-white/15 px-2 py-1 text-[11px] text-text-secondary transition hover:border-white/25 hover:text-text-primary disabled:opacity-60"
      >
        {downloading ? 'Laster…' : 'Last ned'}
      </button>
    </div>
  );
}

interface KindedAttachment {
  index: number;
  attachment: EventAttachment;
  kind: AttachmentKind;
}

function attachmentKey(attachment: EventAttachment, index: number): string {
  return attachment.sourceItemId ?? attachment.externalId ?? String(index);
}

export function AttachmentList({
  caseId,
  attachments,
}: {
  caseId: string;
  attachments: EventAttachment[];
}) {
  const { downloadingSourceItemId, downloadError, downloadAttachment } = useAttachmentDownload();
  const viewer = useAttachmentViewer();

  const items: KindedAttachment[] = attachments.map((attachment, index) => ({
    index,
    attachment,
    kind: attachmentKind(attachment),
  }));
  const images = items.filter((item) => item.kind.isImage);
  const files = items.filter((item) => !item.kind.isImage);

  const isDownloading = (attachment: EventAttachment): boolean =>
    downloadingSourceItemId === attachment.sourceItemId;
  const download = (attachment: EventAttachment): void => {
    if (attachment.sourceItemId) {
      void downloadAttachment(caseId, attachment.sourceItemId);
    }
  };
  const openViewer = (index: number): void => viewer.open(index);

  return (
    <div className="flex flex-col gap-2.5">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map(({ attachment, index }) => (
            <AttachmentImageButton
              key={attachmentKey(attachment, index)}
              caseId={caseId}
              attachment={attachment}
              onOpen={() => openViewer(index)}
            />
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {files.map(({ attachment, index }) => (
            <AttachmentFileRow
              key={attachmentKey(attachment, index)}
              attachment={attachment}
              onOpen={() => openViewer(index)}
              downloading={isDownloading(attachment)}
              onDownload={() => download(attachment)}
            />
          ))}
        </div>
      )}

      {downloadError && <p className="mt-1 text-xs text-red-300">{downloadError}</p>}

      <AttachmentViewer
        caseId={caseId}
        items={items}
        openIndex={viewer.openIndex}
        onClose={viewer.close}
        onNext={() => viewer.next(items.length)}
        onPrev={() => viewer.prev(items.length)}
        onDownload={download}
        isDownloading={isDownloading}
      />
    </div>
  );
}
